import { useEffect, useState, useCallback, useRef } from 'react';
import '@style/TestSections.scss';
import RightTimeApi from "@/API";
import { toast } from 'react-toastify';
import { DateTime } from 'luxon';
import { LuTimerOff } from "react-icons/lu";
import SectionRenderer from './SectionRenderer';
import type { SectionRealtimePayload, SectionCompletedEvent, SectionRendererHooks } from './sectionRuntimeTypes';
import {
    buildNextSectionRealtimePostData,
    isSectionPersistenceVerified,
    realtimeEventsAfterSectionComplete,
} from '@/utils/testSectionCompletionHelpers';
import { getSectionDraftStorageKey } from '@/utils/sectionDraftStorage';
import {
    dequeueOfflineQueueHead,
    enqueueOfflineItem,
    readOfflineQueue as readOfflineQueueStorage,
    writeOfflineQueue as writeOfflineQueueStorage,
} from '@/utils/offlineQueueStorage';
import { apiMessage } from '@/utils/apiMessage';
import { sectionResumeFor } from '@/utils/restartRealtime';

export default function TestSections(props) {
    const { testState, updateTestState, updateSpinner } = props || {};
    const [issue, updateIssue] = useState(null);
    const [offline, setOffline] = useState(false);
    const [showOfflinePage, setShowOfflinePage] = useState(false);
    const [offlineQueue, setOfflineQueue] = useState([]);
    const [syncStartTotal, setSyncStartTotal] = useState(0);
    const offlineQueueRef = useRef([]);
    const offlineTimerRef = useRef(null);
    const offlineRef = useRef(false);
    const isProcessingOfflineQueueRef = useRef(false);
    const lastCompletionIssueCountRef = useRef(0);
    const sectionOopsCountRef = useRef(0);
    const [currentTestType, updateCurrentTestType] = useState(null);
    const [currentSection, updateCurrentSection] = useState(testState?.currentSection || null);
    const testStateRef = useRef(testState);
    const updateSpinnerRef = useRef(updateSpinner);
    const sendRealtimeRef = useRef(null);
    const pendingTotalCount = offlineQueue.length;

    function buildSectionDraftKey(sectionId = null) {
        const resolvedTestId = Number(testStateRef.current?.testData?.id || testState?.testData?.id || 0);
        const resolvedSectionId = Number(sectionId || currentSection?.id || 0);
        return getSectionDraftStorageKey(resolvedTestId, resolvedSectionId);
    }

    function buildSectionFieldKey(fieldId, formId) {
        const fieldIdStr = String(fieldId ?? '').trim();
        const match = fieldIdStr.match(/^(\d+)([a-zA-Z]*)$/);
        if (!match) return fieldIdStr;

        const numericPart = Number(match[1]);
        const alphaPart = match[2] || '';
        const resolvedFormId = Number(formId || 0);
        const baseKey = numericPart > 100 ? String(numericPart) : String(resolvedFormId + numericPart);
        return `${baseKey}${alphaPart}`;
    }

    function readSectionDraft(sectionId = null) {
        try {
            const raw = localStorage.getItem(buildSectionDraftKey(sectionId));
            if (!raw) return { values: {}, localTimes: {} };
            const parsed = JSON.parse(raw);
            return {
                values: parsed?.values && typeof parsed.values === 'object' ? parsed.values : {},
                localTimes: parsed?.localTimes && typeof parsed.localTimes === 'object' ? parsed.localTimes : {},
            };
        } catch {
            return { values: {}, localTimes: {} };
        }
    }

    function upsertSectionDraft(answerEvent) {
        const questionId = answerEvent?.questionId;
        if (questionId === null || questionId === undefined) return;

        const sectionId = Number(answerEvent?.sectionId || currentSection?.id || 0);
        const fieldKey = buildSectionFieldKey(questionId, answerEvent?.formId);
        if (!fieldKey) return;

        const response = String(answerEvent?.response ?? '');
        const now = DateTime.now().toISO();
        const existing = readSectionDraft(sectionId);
        const nextValues = { ...(existing.values || {}) };

        if (response === '')
            delete nextValues[fieldKey];
        else
            nextValues[fieldKey] = response;

        const nextLocalTimes = {
            ...(existing.localTimes || {}),
            [fieldKey]: now,
        };

        localStorage.setItem(
            buildSectionDraftKey(sectionId),
            JSON.stringify({ values: nextValues, localTimes: nextLocalTimes })
        );
    }

    function clearSectionDraftForSection(sectionId = null) {
        localStorage.removeItem(buildSectionDraftKey(sectionId));
    }

    function getResolvedTestId() {
        return Number(testStateRef.current?.testData?.id || testState?.testData?.id || 0);
    }

    function writeOfflineQueue(queue) {
        writeOfflineQueueStorage(localStorage, getResolvedTestId(), queue);
    }

    function readOfflineQueue() {
        return readOfflineQueueStorage(localStorage, getResolvedTestId());
    }

    function setOfflineQueueState(queue) {
        const safeQueue = Array.isArray(queue) ? queue : [];
        offlineQueueRef.current = safeQueue;
        setOfflineQueue(safeQueue);
        writeOfflineQueue(safeQueue);
    }

    function setOfflineState(nextOffline) {
        offlineRef.current = Boolean(nextOffline);
        setOffline(Boolean(nextOffline));
    }

    useEffect(() => {
        if(testState && testState.testTypes && testState.testTypes.length > 0) {
            updateCurrentTestType(testState.testTypes[0]);
        }
    }, [testState]);

    useEffect(() => {
        testStateRef.current = testState;
        updateSpinnerRef.current = updateSpinner;
    }, [testState, updateSpinner]);

    useEffect(() => {
        if(!props?.testState || !currentTestType) return;

        // Once a section is active (or provided by server state), do not recompute
        // from lastSectionIdCompleted; that can fight normal next-section flow.
        if (currentSection?.id || testState?.currentSection?.id) return;

       if(testState && !currentSection && 
            testState?.lastSectionIdCompleted===0 && 
            currentTestType?.sections?.length>0) {
            localStorage.setItem('sectionOrder', String(currentTestType.sections[0]?.order));
        }
        if (!testState.isDebug && currentTestType?.sections?.length) {
            let sectionToSet = null;
            if (testState.lastSectionIdCompleted) {
                const lastSection = currentTestType.sections.find(s => s.id === testState.lastSectionIdCompleted);
                if (lastSection) {
                    sectionToSet = currentTestType.sections
                        .filter(s => s.order > lastSection.order)
                        .sort((a, b) => a.order - b.order)[0];
                }
            }
            if (!sectionToSet) 
                sectionToSet = currentTestType.sections.sort((a,b)=> a.order-b.order)[0];

            if (sectionToSet) {
                localStorage.setItem('sectionOrder', String(sectionToSet.order));
                if (!currentSection || (currentSection && currentSection?.id !== sectionToSet.id)) 
                    updateCurrentSection(sectionToSet);
            }
        }
    }, [currentTestType, currentSection?.id, props?.testState, testState]);

    useEffect(() => {
        if (!testState?.currentSection) return;
        if (currentSection?.id !== testState.currentSection.id) {
            updateCurrentSection(testState.testTypes?.[0]?.sections?.find(s => s.id === testState.currentSection.id) || null);
        }
    }, [testState?.currentSection, currentSection?.id, testState?.page, testState?.testTypes]);

    useEffect(() => {
        // Oops counter is section-scoped.
        sectionOopsCountRef.current = 0;
    }, [currentSection?.id]);

    useEffect(() => {
        if (!showOfflinePage) {
            setSyncStartTotal(0);
            return;
        }
        if (pendingTotalCount > syncStartTotal) {
            setSyncStartTotal(pendingTotalCount);
            return;
        }
        if (syncStartTotal === 0 && pendingTotalCount > 0) {
            setSyncStartTotal(pendingTotalCount);
        }
    }, [showOfflinePage, pendingTotalCount, syncStartTotal]);
    
    const processOfflineQueue = useCallback(async () => {
        // Prevent concurrent interval ticks from racing and rewriting queue state.
        if (isProcessingOfflineQueueRef.current) return;
        isProcessingOfflineQueueRef.current = true;

        if (offlineQueueRef.current.length === 0) {
            setOfflineQueueState([]);
            isProcessingOfflineQueueRef.current = false;
            return;
        }

        let connectionRestoredTime = null;
        let postSectionResponse = null;
        let drainFailed = false;
        try {
            // Drain from the live queue head so answers enqueued mid-flush are not dropped.
            while (offlineQueueRef.current.length > 0) {
                const item = offlineQueueRef.current[0];
                try {
                    const resp = await RightTimeApi.postAsync(item.url, item.postData);
                    if (resp && resp.ok) {
                        if (item.url.includes("PostSection") && !isSectionPersistenceVerified(resp)) {
                            toast.error("Your answers could not be saved. Please try again or contact your recruiter.");
                            drainFailed = true;
                            break;
                        }

                        if (!connectionRestoredTime) {
                            setOfflineState(false);
                            connectionRestoredTime = DateTime.now().toISO();
                        }

                        setOfflineQueueState(dequeueOfflineQueueHead(offlineQueueRef.current));

                        if (item.url.includes("PostSection")) {
                            postSectionResponse = resp;
                            clearSectionDraftForSection(item?.postData?.SectionId);
                            const envelope = buildNextSectionRealtimePostData(
                                testStateRef.current,
                                null,
                                item.postData?.OopsCount ?? 0,
                                item.postData
                            );
                            const completionEvents = realtimeEventsAfterSectionComplete(
                                item.postData?.Name,
                                envelope.FormId,
                                item.postData?.values,
                                item.postData?.OopsCount ?? 0,
                            );
                            for (const event of completionEvents) {
                                await RightTimeApi.postAsync("/RealTimeResponse", {
                                    ...envelope,
                                    questionId: event.questionId,
                                    response: event.response,
                                });
                            }
                        }
                    } else {
                        drainFailed = true;
                        break; // Stop if any item fails
                    }
                } catch {
                    drainFailed = true;
                    setOfflineState(true);
                    if (!offlineTimerRef.current)
                        offlineTimerRef.current = setInterval(processOfflineQueue, 5000);
                    break; // Stop if any item throws
                }
            }

            if (offlineQueueRef.current.length === 0 && offlineTimerRef.current) {
                clearInterval(offlineTimerRef.current);
                offlineTimerRef.current = null;
            }

            if (connectionRestoredTime) {
                const postData = {
                    Code: testStateRef.current?.testData?.code ?? "demo",
                    LanguageId: testStateRef.current?.testData?.languageId ?? 0,
                    TestId: testStateRef.current?.testData?.id ?? 0,
                    CompanyId: testStateRef.current?.testData?.companyId ?? 0,
                    questionId: "ConnectionRestored",
                    response: "1",
                    ClientTime: connectionRestoredTime,
                    When: connectionRestoredTime
                }
                await RightTimeApi.postAsync("/RealTimeResponse", postData);
                if (postSectionResponse && offlineQueueRef.current.length === 0) {
                    setShowOfflinePage(false);
                    const newSection = testStateRef.current?.testTypes?.[0]?.sections
                        ?.find(s => s.sectionId === postSectionResponse.jsonData?.sectionId) || null;
                    updateCurrentSection(newSection);
                    updateSpinnerRef.current?.(false);
                }
            }
        } finally {
            isProcessingOfflineQueueRef.current = false;
        }

        // Items may have been enqueued after the while loop emptied the queue but before the lock cleared.
        // Do not recurse after a failed send — the interval handles retries.
        if (!drainFailed && offlineQueueRef.current.length > 0) {
            void processOfflineQueue();
        }
    }, []);

    useEffect(() => {
        const testId = Number(testState?.testData?.id || 0);
        if (!testId) return;

        const restoredQueue = readOfflineQueue();
        if (restoredQueue.length === 0) return;

        setOfflineState(true);
        setOfflineQueueState(restoredQueue);
        if (!offlineTimerRef.current)
            offlineTimerRef.current = setInterval(processOfflineQueue, 5000);

        // Try sending immediately in case connectivity already returned.
        void processOfflineQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [testState?.testData?.id, processOfflineQueue]);

    useEffect(() => {
        const testId = Number(testState?.testData?.id || 0);
        if (!testId || testState?.isDebug || !testState?.currentSection) return undefined;

        let hiddenAt = null;

        const onVisibility = () => {
            if (document.visibilityState === "hidden") {
                hiddenAt = Date.now();
                return;
            }
            if (hiddenAt == null) return;
            const hiddenMs = Date.now() - hiddenAt;
            hiddenAt = null;
            if (hiddenMs < 1000) return;
            void sendRealtimeRef.current?.({ questionId: "PageHidden", response: String(Math.round(hiddenMs)) });
        };

        const onPaste = () => {
            void sendRealtimeRef.current?.({ questionId: "Paste", response: "1" });
        };

        document.addEventListener("visibilitychange", onVisibility);
        document.addEventListener("paste", onPaste);
        return () => {
            document.removeEventListener("visibilitychange", onVisibility);
            document.removeEventListener("paste", onPaste);
        };
    }, [testState?.testData?.id, testState?.isDebug, testState?.currentSection]);

    // Conditional return AFTER all hooks have been called
    if (!props || !testState || !updateSpinner || !testState.currentSection)
        return null;
    
    const sectionData = {
        testState,
        lang: testState?.lang,
        currentSection: currentSection,
        sectionControls: testState?.sectionControls,
        sectionControlsByAbbrev: testState?.sectionControlsByAbbrev,
    };

    const rendererHooks: SectionRendererHooks = {
        onAnswer: onAnswer,
        onPageChanged: onPageChangedAsync,
        onNextAttempt: onNextAttemptAsync,
        onSectionCompleted: onSectionCompletedAsync,
    };

    function getOrderedSections() {
        if (!testState?.testTypes?.[0]?.sections?.length) return [];
        return [...testState.testTypes[0].sections].sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
    }

    function getNextSectionCandidate() {
        const orderedSections = getOrderedSections();
        if (!orderedSections.length) return null;
        let next = null;

        const currentId = Number(currentSection?.id || 0);
        if (currentId > 0) {
            const currentIndex = orderedSections.findIndex((s) => Number(s.id || 0) === currentId);
            if (currentIndex >= 0) {
                next = orderedSections[currentIndex + 1] || null;
            }
        }

        if (!next) {
            const currentOrder = Number(currentSection?.order);
            if (Number.isFinite(currentOrder)) {
                next = orderedSections.find((s) => Number(s.order || 0) > currentOrder) || null;
            }
        }

        if (!next) {
            return null;
        }

        return next;
    }

    function isDoneSection(section) {
        const name = String(section?.name || "").trim().toLowerCase();
        return name === "done";
    }

    function shouldBlockBeforeDoneWhenOffline() {
        const next = getNextSectionCandidate();
        if (!next) return true;
        return isDoneSection(next);
    }

    function getNextSection()   {
        const next = getNextSectionCandidate();
        if (!next) return null;

        next.page = 1;
        updateCurrentSection(next);
        updateTestState({
            ...testState,
            lastSectionIdCompleted: Number(currentSection?.id || 0),
            currentSection: next
        });
        localStorage.setItem('sectionOrder', String(next.order || 0));
        return next;
    }

    async function onAnswer(answerEvent: SectionRealtimePayload) {
        const questionId = answerEvent?.questionId;
        const responseValue = answerEvent?.response;
        if (questionId === null || questionId === undefined) {
            return;
        }

        upsertSectionDraft(answerEvent);

        await sendRealtimeResponseAsync({
            questionId: String(questionId),
            response: String(responseValue ?? ""),
        });
    }

    async function onSectionCompletedAsync(sectionEvent: SectionCompletedEvent) {
        const oopsCount = Number(sectionOopsCountRef.current || 0);
        const values = sectionEvent?.values || {};
        const valueTimes = sectionEvent?.valueTimes || {};
        const sectionDraft = readSectionDraft(sectionEvent?.sectionId);
        const mergedValues = {
            ...(sectionDraft.values || {}),
            ...values,
        };
        const mergedValueTimes = {
            ...(sectionDraft.localTimes || {}),
            ...valueTimes,
        };
        lastCompletionIssueCountRef.current = oopsCount;

        updateSpinner(true);
        const resp = await onNextSectionPostAsync(mergedValues, mergedValueTimes, oopsCount);
        if (resp && resp.ok) {
            if (!isSectionPersistenceVerified(resp)) {
                toast.error("Your answers could not be saved. Please try again or contact your recruiter.");
                updateSpinner(false);
                return;
            }
            const completionEvents = realtimeEventsAfterSectionComplete(
                currentSection?.name,
                currentSection?.formId,
                mergedValues,
                Number(sectionEvent?.response ?? oopsCount),
            );
            for (const event of completionEvents) {
                await sendRealtimeResponseAsync({
                    questionId: event.questionId,
                    response: event.response,
                });
            }
            clearSectionDraftForSection(sectionEvent?.sectionId);
            getNextSection();
        } else if (resp && resp.offline) {
            if (shouldBlockBeforeDoneWhenOffline()) {
                setShowOfflinePage(true);
            } else {
                getNextSection();
            }
        }
        updateSpinner(false);
    }

    async function onPageChangedAsync(pageEvent: SectionRealtimePayload) {
        await sendRealtimeResponseAsync({ questionId: String(pageEvent?.questionId || "NextPage"), response: String(pageEvent?.response ?? 0) });
    }

    async function onNextAttemptAsync(nextAttemptEvent: SectionRealtimePayload) {
        const issueCount = Number(nextAttemptEvent?.response || 0);
        if (issueCount <= 0) {
            return;
        }

        // Each failed Next click counts as one "oops".
        sectionOopsCountRef.current = Number(sectionOopsCountRef.current || 0) + 1;
        await sendRealtimeResponseAsync({ questionId: String(nextAttemptEvent?.questionId || "NextPage"), response: issueCount.toString() });
    }

    async function onNextSectionPostAsync(values, valueTimes, oopsCount) {
        if(!testState || testState.isDebug) {
            console.log("Debug mode - skipping PostSection");
            return null;
        }

        const postData = {
            TestId: testState.testData.id,
            Name: currentSection?.name,
            SectionId: currentSection?.id,
            FormId: currentSection?.formId,
            Success: true,
            Code: testState.testData.code,
            values,
            localTimes: valueTimes,
            OopsCount: oopsCount
        };
        updateSpinner(true);

        const resp = await sendData("/PostSection", postData);
        if (resp) {
            if (!resp.offline) {
                updateSpinner(false);
                if (resp && !resp.ok) {
                    if (resp.jsonData.issueType)
                        updateIssue(resp.jsonData);
                    else {
                        const msg = apiMessage(resp);
                        if (msg) toast.error(msg);
                    }
                }
            }
        }
        return resp;
    }

    function renderIssueType(issueType) {
        switch (issueType) {
            case "TimedOut":
                return <LuTimerOff className="issueIcon" />;
            default:
                return null;
        }
    }


    async function sendRealtimeResponseAsync(retData) {
        const clientTime = DateTime.now().toISO();
        const postData = {
            ...retData,
            Name: currentSection?.name || "Request",
            FormId: currentSection?.formId,
            SectionId: currentSection?.id,
            Code: testState?.testData?.code ?? "demo",
            LanguageId: testState?.languageId,
            TestId: testState?.testData?.id ?? 0,
            CompanyId: testState?.testData?.companyId ?? 0,
            ClientTime: clientTime,
            When: clientTime
        };
        if (!testState.isDebug) {
            return await sendData("/RealTimeResponse", postData);
        }
    }

    sendRealtimeRef.current = sendRealtimeResponseAsync;

    async function sendData(url, data) {
        try {
            if (offlineRef.current) {
                setOfflineQueueState(enqueueOfflineItem(offlineQueueRef.current, { url: url, postData: data }));
                if (!offlineTimerRef.current)
                    offlineTimerRef.current = setInterval(processOfflineQueue, 5000);
                return { offline: true, url: url, data: data };
            }
            else {
                const resp = await RightTimeApi.postAsync(url, data);
                updateSpinner(false);
                if (resp && !resp.ok) {
                    if (resp.jsonData.issueType)
                        updateIssue(resp.jsonData);
                    else {
                        const msg = apiMessage(resp);
                        if (msg) toast.error(msg);
                    }
                }
                return resp;
            }
        } catch (err) {
            // API call failed, go offline and queue the request
            setOfflineState(true);

            localStorage.setItem('sectionOrder', String(currentSection.order));


            // Use ref to ensure latest queue
            let arr = enqueueOfflineItem(offlineQueueRef.current, { url: url, postData: data });
            if (arr.length === 1) {
                arr = enqueueOfflineItem(arr, {
                    url: "/RealTimeResponse", postData: {
                        Name: currentSection?.name || "Request",
                        FormId: currentSection?.formId,
                        SectionId: currentSection?.id,
                        Code: testState?.testData?.code ?? "demo",
                        LanguageId: testState?.languageId,
                        TestId: testState?.testData?.id ?? 0,
                        CompanyId: testState?.testData?.companyId ?? 0,
                        questionId: "ConnectionLost",
                        response: "1",
                        ClientTime: DateTime.now().toISO(),
                        When: DateTime.now().toISO()
                    }
                });
            }
            setOfflineQueueState(arr);
            if (!offlineTimerRef.current) {
                offlineTimerRef.current = setInterval(processOfflineQueue, 5000); // try every 5s
            }
            // If a reconnect flush is already running, the live-head drain will pick these up.
            if (!isProcessingOfflineQueueRef.current) {
                void processOfflineQueue();
            }
            return { offline: true, url: url, data: data, err: err };
        }
    }

    function renderIssue() {
        return (
            <div className="issueContainer">
                {renderIssueType(issue?.issueType)}
                {issue && issue.msg && <span className="issueMessage">{issue.msg}</span>}
            </div>
        );
    }

    function renderDisconnectedView() {
        return (
            <div className="offlinePage offlinePage--disconnected">
                <h2>Connection Lost!</h2>
                <p>You have lost connection to the internet. Your answers have not been lost.</p>
                <p>Please do whatever you can to restore the connection.</p>
                <p>Do NOT close this browser window if at all possible.</p>
            </div>
        );
    }

    function renderRestoredSyncView() {
        return (
            <div className="offlinePage offlinePage--restored">
                <h2>Connection Restored</h2>
                <p>Your connection has been restored. Sending stored answer data now.</p>
                <div className="offlineSyncProgress" role="status" aria-live="polite">
                    <progress
                        className="offlineSyncProgressTrack"
                        max={Math.max(1, syncStartTotal)}
                        value={Math.max(0, syncStartTotal - pendingTotalCount)}
                    />
                    <p className="offlineSyncProgressText">
                        {pendingTotalCount > 0
                            ? `Syncing stored answer data... ${pendingTotalCount} remaining.`
                            : 'All stored answer data sent.'}
                    </p>
                </div>
                <p>Please keep this browser window open until sync completes.</p>
            </div>
        );
    }

    // console.log("currentSection:", currentSection);
    // console.log("testState:", testState);
    const resumeForSection = sectionResumeFor(
        testState?.sectionResume,
        Number(currentSection?.id || currentSection?.sectionId || 0)
    );

    if (!testState.currentSection || !sectionData) return null;

    return (
        <>
            {offline && <div className="offlineStatusDot" aria-hidden="true" />}
            {issue ? renderIssue() : (
                <>
                    {showOfflinePage ? (
                        (offline ? renderDisconnectedView() : renderRestoredSyncView())
                    ) : (
                        <SectionRenderer
                            key={Number(currentSection?.id || 0)}
                            sectionData={sectionData}
                            hooks={rendererHooks}
                            initialPage={resumeForSection?.page}
                            initialValues={resumeForSection?.answers}
                        />)}
                </>)
            }
        </>
    );
};
